/**
 * Estilos do Terminal Kiosk — F8.2 (ADR-0023 D11).
 *
 * Diretrizes:
 *   - Touch-friendly: alvos minimos 56px, fontes 18px+
 *   - Alto contraste para chao de fabrica (luz, oleo, sujeira nos olhos)
 *   - Cores semanticas: verde/vermelho/amarelo/cinza
 *   - Background neutro escuro (reduz fadiga visual em turnos longos)
 */

import styled, { keyframes, css } from "styled-components";

export const KioskShell = styled.div`
  position: fixed;
  inset: 0;
  background: #1a1d23;
  color: #e8eaed;
  font-family: system-ui, -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const KioskHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #0f1419;
  border-bottom: 2px solid #2a2f37;
  font-size: 16px;

  .marca {
    font-weight: 800;
    letter-spacing: 1px;
    color: #d97706;
  }
  .ctx {
    display: flex;
    gap: 16px;
    align-items: center;
    color: #a4adba;
    font-size: 14px;
  }
  .ctx strong {
    color: #ffffff;
    font-size: 16px;
  }
`;

export const KioskBody = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  overflow: auto;
`;

export const KioskCard = styled.section`
  background: #242830;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

export const Title = styled.h1`
  margin: 0 0 24px;
  font-size: 28px;
  font-weight: 800;
  color: #ffffff;
  text-align: center;
`;

export const Subtitle = styled.p`
  margin: -16px 0 32px;
  text-align: center;
  color: #a4adba;
  font-size: 16px;
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #a4adba;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const FieldRow = styled.div`
  margin-bottom: 24px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #3a4049;
  border-radius: 8px;
  background: #1a1d23;
  color: #ffffff;
  text-align: center;
  letter-spacing: 4px;

  &:focus {
    outline: none;
    border-color: #d97706;
  }
  &::placeholder {
    color: #4a5260;
    letter-spacing: 1px;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 16px 20px;
  font-size: 18px;
  font-weight: 600;
  border: 2px solid #3a4049;
  border-radius: 8px;
  background: #1a1d23;
  color: #ffffff;

  &:focus {
    outline: none;
    border-color: #d97706;
  }
`;

const buttonBase = css`
  width: 100%;
  padding: 20px 32px;
  font-size: 20px;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.05s ease, filter 0.15s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-height: 64px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:not(:disabled):active {
    transform: translateY(1px);
    filter: brightness(0.9);
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: #d97706;
  color: #ffffff;

  &:not(:disabled):hover {
    background: #ea8512;
  }
`;

export const SuccessButton = styled.button`
  ${buttonBase}
  background: #16a34a;
  color: #ffffff;

  &:not(:disabled):hover {
    background: #1cb755;
  }
`;

export const DangerButton = styled.button`
  ${buttonBase}
  background: #dc2626;
  color: #ffffff;

  &:not(:disabled):hover {
    background: #ef4444;
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: #a4adba;
  border: 2px solid #3a4049;

  &:not(:disabled):hover {
    background: #2a2f37;
    color: #ffffff;
  }
`;

export const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Alert = styled.div`
  padding: 16px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  animation: ${slideIn} 0.25s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  ${(p) => p.$tone === "success" && css`
    background: rgba(22, 163, 74, 0.18);
    color: #4ade80;
    border-left: 4px solid #16a34a;
  `}
  ${(p) => p.$tone === "danger" && css`
    background: rgba(220, 38, 38, 0.18);
    color: #fca5a5;
    border-left: 4px solid #dc2626;
  `}
  ${(p) => p.$tone === "warning" && css`
    background: rgba(217, 119, 6, 0.18);
    color: #fbbf24;
    border-left: 4px solid #d97706;
  `}
  ${(p) => p.$tone === "info" && css`
    background: rgba(56, 132, 209, 0.18);
    color: #93c5fd;
    border-left: 4px solid #3884d1;
  `}
`;

export const OpContextoBox = styled.div`
  background: #1a1d23;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #3a4049;

  .numero {
    font-size: 28px;
    font-weight: 800;
    color: #d97706;
    margin-bottom: 8px;
  }
  .desc {
    color: #e8eaed;
    font-size: 16px;
    margin-bottom: 16px;
  }
  .meta {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    font-size: 14px;
    color: #a4adba;
  }
  .meta strong {
    color: #ffffff;
    font-weight: 700;
  }
`;

export const Pill = styled.span`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;

  ${(p) => p.$tone === "success" && css`
    background: rgba(22, 163, 74, 0.2);
    color: #4ade80;
  `}
  ${(p) => p.$tone === "warning" && css`
    background: rgba(217, 119, 6, 0.2);
    color: #fbbf24;
  `}
  ${(p) => p.$tone === "danger" && css`
    background: rgba(220, 38, 38, 0.2);
    color: #fca5a5;
  `}
  ${(p) => p.$tone === "info" && css`
    background: rgba(56, 132, 209, 0.2);
    color: #93c5fd;
  `}
  ${(p) => p.$tone === "neutral" && css`
    background: #2a2f37;
    color: #a4adba;
  `}
`;

export const HistoricoRecente = styled.div`
  background: #1a1d23;
  border-radius: 8px;
  padding: 16px 20px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #2a2f37;

  h3 {
    margin: 0 0 12px;
    font-size: 12px;
    color: #a4adba;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #2a2f37;
    font-size: 14px;
    color: #e8eaed;
  }
  li:last-child { border-bottom: none; }
  li .right {
    color: #a4adba;
    font-size: 12px;
  }
`;

export const Spacer = styled.div`
  height: ${(p) => p.$h || 16}px;
`;

export const ExpiraEmIndicator = styled.div`
  font-size: 12px;
  color: ${(p) => (p.$alerta ? "#fbbf24" : "#4a5260")};
`;
